import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  TemplateRef,
  computed,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { FormControl, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms'

import { MenuModule } from 'primeng/menu'
import { TooltipModule } from 'primeng/tooltip'

import { FlxOrderByPipe } from '@flowx/angular-sdk'
import {
  FlxButtonModule,
  FlxErrorComponent,
  FlxHelperComponent,
  FlxIconModule,
  FlxInputComponent,
  FlxInputDirective,
  FlxLabelComponent,
  FlxModalService,
} from '@flowx/angular-ui-toolkit'
import { ArchiveDefaultMenuItem } from '@flowx/designer/modules/projects/components/resources-page/menu-items.constants'

import { FlxDateFormatPipe } from '../../../pipes/flx-date.pipe'
import { FlxUserInitialsPipe } from '../../../pipes/user-initials.pipe'
import { ConfirmationModalComponent } from '../../components/confirmation-modal/confirmation-modal.component'
import { IdeaHelperBoxComponent } from '../../components/idea-helper-box/idea-helper-box.component'
import { ApplicationVersion, BranchDeleteState } from '../../entities/application.entity'
import { BranchNameComponent } from '../branch-name/branch-name.component'
import { FlxBranchStateComponent } from '../branch-state/branch-state.component'
import {
  VersionActionConfig,
  VersionActionEvent,
  VersionActionType,
  VersionActionsMap,
} from '../version-actions.constants'

@Component({
  selector: 'flx-branch-info',
  imports: [
    FlxButtonModule,
    FlxIconModule,
    MenuModule,
    BranchNameComponent,
    FlxUserInitialsPipe,
    FlxDateFormatPipe,
    TooltipModule,
    FlxInputComponent,
    FlxLabelComponent,
    FormsModule,
    FlxInputDirective,
    IdeaHelperBoxComponent,
    FlxBranchStateComponent,
    FlxOrderByPipe,
    FlxErrorComponent,
    FlxHelperComponent,
    ReactiveFormsModule,
  ],
  templateUrl: './branch-info.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FlxBranchInfoComponent {
  #flxModalService = inject(FlxModalService)
  #destroyRef = inject(DestroyRef)
  private readonly archiveBranchTRef = viewChild<TemplateRef<{ deleteState: BranchDeleteState }>>(
    'archiveBranchConfirmation'
  )
  private readonly unarchiveBranchTRef = viewChild<
    TemplateRef<{
      applicationVersion: ApplicationVersion
      branchName: string
      versionCount: number
    }>
  >('unarchiveBranchConfirmation')

  applicationVersion = input.required<ApplicationVersion>()
  createdFromVersion = input<ApplicationVersion | null>(null)
  deleteBranchState = input.required<BranchDeleteState>()
  actions =
    input.required<
      Pick<
        VersionActionsMap,
        | VersionActionType.ArchiveBranch
        | VersionActionType.UnarchiveBranch
        | VersionActionType.Merge
      >
    >()

  actionTriggered = output<VersionActionEvent>()

  archiveReasonFC = new FormControl('', { validators: [Validators.required] })
  archivedActionSubmitted = signal(false)

  // Actions rendered as standalone buttons under the branch details (kebab items excluded)
  standaloneActions = computed(() => [
    this.actions()[VersionActionType.Merge],
    this.actions()[VersionActionType.UnarchiveBranch],
  ])

  contextMenuItems = computed(() => {
    const archiveAction = this.actions()[VersionActionType.ArchiveBranch]
    const deleteBranchState = this.deleteBranchState()
    return [
      {
        ...ArchiveDefaultMenuItem(
          !archiveAction.enabled,
          archiveAction.visible,
          archiveAction.label
        ),
        command: () => this.#startArchiveBranch(deleteBranchState),
        tooltipOptions: {
          tooltipLabel: archiveAction.tooltip,
          tooltipStyleClass: 'text-xs',
        },
      },
    ].filter((action) => action.visible)
  })

  onStandaloneAction(config: VersionActionConfig): void {
    // Unarchive goes through its confirmation modal, which emits after confirm
    if (config.action === VersionActionType.UnarchiveBranch) {
      this.startUnarchiveBranch()
      return
    }
    this.actionTriggered.emit({ action: config.action } as VersionActionEvent)
  }

  startUnarchiveBranch(): void {
    const branchName = this.applicationVersion().branchName.split('#')[0]
    this.#flxModalService
      .show(
        ConfirmationModalComponent,
        { closeOnBackdropClick: false, backdropClass: 'overlay-backdrop' },
        {
          title: `Unarchive Branch ${branchName}`,
          subtitle: 'Branch archived and hidden from the tree view',
          applicationVersion: this.applicationVersion(),
          branchName,
          versionCount: this.deleteBranchState().branchVersions,
          customTemplate: this.unarchiveBranchTRef(),
          btnOkText: 'Unarchive',
          btnOkType: 'primary',
        }
      )
      .data$.pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(({ confirm }) => {
        if (confirm) {
          this.actionTriggered.emit({
            action: VersionActionType.UnarchiveBranch,
            payload: this.applicationVersion(),
          })
        }
      })
  }

  #startArchiveBranch(deleteState: BranchDeleteState): void {
    this.archivedActionSubmitted.set(false)
    this.#flxModalService
      .show(
        ConfirmationModalComponent,
        { closeOnBackdropClick: false, backdropClass: 'overlay-backdrop' },
        {
          title: `Archive Branch ${deleteState.appVersion.branchName}`,
          subtitle:
            'Once archived it will be hidden from the tree view. You can unarchive it anytime',
          deleteState,
          customTemplate: this.archiveBranchTRef(),
          btnOkText: 'Archive Branch',
          closeModalOnConfirm: false,
        }
      )
      .data$.pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(({ confirm }) => {
        this.archivedActionSubmitted.set(true)
        if (!confirm) {
          this.#hideConfirmationModal()
          return
        }
        if (this.archiveReasonFC.invalid) {
          return
        }
        this.actionTriggered.emit({
          action: VersionActionType.ArchiveBranch,
          payload: {
            appVersion: deleteState.appVersion,
            reason: this.archiveReasonFC.value.trim(),
          },
        })
        this.#hideConfirmationModal()
      })
  }

  #hideConfirmationModal(): void {
    this.archiveReasonFC.reset()
    this.archivedActionSubmitted.set(false)
    this.#flxModalService.hide()
  }
}
